// customLabPanel.ts — P2.4 CUSTOM LAB: the framed entry for the existing custom-topic flow.
//
// A modal where the player CRAFTS a factual topic (with an optional difficulty "twist"), then taps
// BUILD & PLAY to run it through the EXISTING generate -> verify board pipeline (fetchBoard, wired in
// MenuScene) and race the rival. Recent custom topics are remembered locally so a returning player
// can re-run or tweak a past lab in one tap, and a hint reminds them they can SHARE the exact board
// afterwards (the challenge deep link already exists in share.ts / ResultScene).
//
// Tactical OLED redesign: full-bleed inset modal (default scan top-rail) with a flask header, an
// intro pitch, the YOUR TOPIC tile-input, a TWIST segmented control, a RECENT LABS chip wrap, a
// share hint, and a single BUILD & PLAY CTA. Pure UI over the shared modal + kit + palette/font; no
// Core, no determinism, no network.
//
// DECOUPLED ON PURPOSE: this panel never imports the scene or the net layer. It takes an `onLaunch`
// callback so MenuScene wires it to its own launch funnel (playTopic(raw, 'custom')) without the panel
// reaching into the scene. Text entry uses window.prompt() — the same reliable-from-canvas pattern
// leaderboardPanel.ts uses for the name field — so there is no DOM overlay to position or leak when
// the modal closes.

import Phaser from 'phaser';
import { PALETTE, hex, FONT } from './keys';
import { modal } from './ui';
import { scaledFont } from './settings';
import { audio } from './audio';
import { icon, type IconName } from './icons';
import { labelText, chip, ctaButton, cardPanel } from './ui/kit';

export interface CustomLabOpts {
  /**
   * Launch a crafted topic through the menu's own funnel. MenuScene passes a thin wrapper that fires
   * the `custom_topic_requested` analytics event and calls `playTopic(topicRaw, 'custom')` — so the
   * existing generate->verify pipeline + the `custom_topic_succeeded` event both fire unchanged.
   */
  onLaunch: (topicRaw: string) => void;
  /** Pre-fill the topic field (e.g. echo the last typed topic from the menu's GO box). */
  initialTopic?: string;
  /** Owner-scene hook (e.g. to drop a keyboard guard) when the modal closes. */
  onClose?: () => void;
}

/** Optional difficulty "twist" appended to the topic the player sends to the verifier. */
interface Twist {
  key: string;
  label: string;
  /** Suffix appended to the topic string (empty = plain topic). */
  suffix: string;
}

const TWISTS: Twist[] = [
  { key: 'standard', label: 'Standard', suffix: '' },
  { key: 'expert', label: 'Expert', suffix: ' — expert level' },
  { key: 'kids', label: 'Kids', suffix: ' — kid-friendly' },
];

// Seed ideas shown to a first-time labber (no recents yet). Factual + theme-noun-free; tapping one
// fills the topic field so the player still tweaks before they BUILD & PLAY.
const IDEAS = [
  'World capitals',
  'The periodic table',
  'Norse mythology',
  'Human anatomy',
  'The solar system',
  'Ancient Rome',
];

const RECENTS_KEY = 'trivia.customlab.recents.v1';
const MAX_RECENTS = 6;
const MAX_TOPIC_LEN = 60;

interface LabStep {
  iconName: IconName;
  color: number;
  title: string;
  body: string;
}

const TRUST_STEPS: LabStep[] = [
  { iconName: 'flask', color: PALETTE.scan, title: 'Topic recipe', body: 'Your prompt plus twist becomes the board brief.' },
  { iconName: 'shield', color: PALETTE.correct, title: 'Trust gate', body: 'The run only starts after verification returns.' },
  { iconName: 'gauge', color: PALETTE.armed, title: 'Spend guard', body: 'Cached requests and caps protect the board service.' },
  { iconName: 'share', color: PALETTE.gold, title: 'Replay pin', body: 'The result screen carries the exact-board challenge.' },
];

/** Read remembered custom-lab topics (most-recent first). Tolerant of blocked/malformed storage. */
function readCustomRecents(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const arr: unknown = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter((x): x is string => typeof x === 'string').slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

/** Remember a base topic (un-suffixed) at the front of the custom-lab recents list. Best-effort. */
function rememberCustomRecent(topic: string): void {
  const t = topic.trim();
  if (!t) return;
  try {
    const prev = readCustomRecents().filter((r) => r.toLowerCase() !== t.toLowerCase());
    const next = [t, ...prev].slice(0, MAX_RECENTS);
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    // private mode / blocked storage — recents are best-effort.
  }
}

export function openCustomLab(scene: Phaser.Scene, opts: CustomLabOpts): ReturnType<typeof modal> {
  const W = scene.scale.width;
  const H = scene.scale.height;
  const landscape = W >= 900 && W > H;
  // Full-bleed inset card, default scan top-rail. Title is drawn by hand below so we can prefix the
  // flask glyph, so the modal's own title stays empty.
  const m = modal(scene, { title: '', fullBleed: true, showClose: true, onClose: opts.onClose });
  const { card, cardW, cardH, bodyTop } = m;

  const fs = `${scaledFont(landscape ? 27 : 22, 14)}px`;
  const smallFs = `${scaledFont(landscape ? 14 : 13, 11)}px`;

  let topic = (opts.initialTopic ?? '').trim().slice(0, MAX_TOPIC_LEN);
  let twistIdx = 0;
  let paintSlot = (): void => {};
  let message: Phaser.GameObjects.Text | null = null;

  // ── header: flask + CUSTOM LAB (the top rail + the ✕ close — via showClose — come from modal) ──
  const headerY = -cardH / 2 + 26;
  const flaskX = -cardW / 2 + 28;
  card.add(icon(scene, 'flask', flaskX, headerY, 20, PALETTE.scan));
  card.add(
    scene.add
      .text(flaskX + 18, headerY, 'CUSTOM LAB', {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(Math.max(17, W * 0.032), 15)}px`,
        color: hex(PALETTE.text),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5),
  );

  // ── intro pitch ──
  card.add(
    scene.add
      .text(0, bodyTop + 22, 'Craft any factual topic — build it, verify it, then race the rival on the exact board.', {
        fontFamily: FONT.family,
        fontSize: smallFs,
        color: hex(PALETTE.dim),
        align: 'center',
        wordWrap: { width: cardW - 96 },
      })
      .setOrigin(0.5),
  );

  const editTopic = (): void => {
    let next: string | null = topic;
    try {
      next = window.prompt('Topic to build a verified board from:', topic);
    } catch {
      next = null; // headless / blocked prompt — leave the field unchanged.
    }
    if (next === null) return; // cancelled
    topic = next.trim().slice(0, MAX_TOPIC_LEN);
    message?.setText('');
    paintSlot();
  };

  const addTopicField = (left: number, right: number, labelY: number): number => {
    const fieldW = right - left;
    const fieldH = landscape ? 58 : 64;
    const fieldY = labelY + (landscape ? 44 : 42);
    card.add(labelText(scene, left, labelY, 'YOUR TOPIC', { size: 12 }));

    const fieldG = scene.add.graphics();
    fieldG.fillStyle(PALETTE.tile, 1);
    fieldG.fillRoundedRect(left, fieldY - fieldH / 2, fieldW, fieldH, 10);
    fieldG.lineStyle(2, PALETTE.scan, 0.9);
    fieldG.strokeRoundedRect(left, fieldY - fieldH / 2, fieldW, fieldH, 10);
    const slotTxt = scene.add
      .text(left + 18, fieldY, '', { fontFamily: FONT.family, fontSize: fs, color: hex(PALETTE.text) })
      .setOrigin(0, 0.5);
    card.add([fieldG, slotTxt]);

    message = scene.add
      .text((left + right) / 2, fieldY + fieldH / 2 + 14, '', {
        fontFamily: FONT.family,
        fontSize: smallFs,
        color: hex(PALETTE.armed),
        align: 'center',
        wordWrap: { width: fieldW - 24 },
      })
      .setOrigin(0.5);
    card.add(message);

    paintSlot = (): void => {
      slotTxt.setScale(1);
      if (topic) {
        slotTxt.setText(topic).setColor(hex(PALETTE.text));
      } else {
        slotTxt.setText('Tap to type a topic...').setColor(hex(PALETTE.mute));
      }
      const maxW = fieldW - 36;
      if (slotTxt.width > maxW) slotTxt.setScale(maxW / slotTxt.width);
    };

    const fieldHit = scene.add
      .rectangle((left + right) / 2, fieldY, fieldW, fieldH, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: true });
    fieldHit.on('pointerup', () => {
      audio().playSfx('uiTap', { volume: 0.7 });
      editTopic();
    });
    card.add(fieldHit);
    paintSlot();
    return fieldY + fieldH / 2 + 34;
  };

  const addTwists = (left: number, right: number, labelY: number): number => {
    card.add(labelText(scene, left, labelY, 'TWIST (OPTIONAL)', { size: 12 }));
    const twistY = labelY + 38;
    const twTrackW = right - left;
    const twGap = landscape ? 10 : 8;
    const twPillH = 38;
    const twPillW = (twTrackW - twGap * (TWISTS.length - 1)) / TWISTS.length;
    const twistPills: { g: Phaser.GameObjects.Graphics; t: Phaser.GameObjects.Text; x: number }[] = [];
    const paintTwist = (): void => {
      twistPills.forEach((p, i) => {
        const on = i === twistIdx;
        p.g.clear();
        p.g.fillStyle(PALETTE.surface, on ? 0 : 0.5);
        p.g.fillRoundedRect(p.x, twistY - twPillH / 2, twPillW, twPillH, 10);
        p.g.lineStyle(on ? 2 : 1.25, on ? PALETTE.correct : PALETTE.lineStrong, on ? 1 : 0.7);
        p.g.strokeRoundedRect(p.x, twistY - twPillH / 2, twPillW, twPillH, 10);
        p.t.setColor(hex(on ? PALETTE.correct : PALETTE.text));
      });
    };
    TWISTS.forEach((tw, i) => {
      const px = left + i * (twPillW + twGap);
      const g = scene.add.graphics();
      const t = scene.add
        .text(px + twPillW / 2, twistY, tw.label, {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(13, 11)}px`,
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      const hit = scene.add
        .rectangle(px + twPillW / 2, twistY, twPillW, Math.max(twPillH, 44), 0xffffff, 0.001)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerup', () => {
        audio().playSfx('uiTap', { volume: 0.7 });
        twistIdx = i;
        paintTwist();
      });
      twistPills.push({ g, t, x: px });
      card.add([g, t, hit]);
    });
    paintTwist();
    return twistY + twPillH / 2;
  };

  const addIdeaChips = (left: number, right: number, labelY: number, maxRows: number): number => {
    const recents = readCustomRecents();
    const isRecents = recents.length > 0;
    const currentTopic = topic.trim().toLowerCase();
    const stripItems = (isRecents ? recents : IDEAS).filter((label) => label.trim().toLowerCase() !== currentTopic);
    card.add(
      labelText(scene, left, labelY, isRecents ? 'RECENT LABS · TAP TO REUSE' : 'NEED AN IDEA? · TAP ONE', {
        size: 12,
      }),
    );

    const rowGap = landscape ? 44 : 42;
    let cx = left;
    let cy = labelY + 38;
    let rows = 1;
    let bottom = cy + 19;
    for (const label of stripItems) {
      const ch = chip(scene, cx, cy, label, {
        upper: false,
        originX: 0,
        textColor: PALETTE.text,
        borderColor: PALETTE.lineStrong,
        height: 38,
        padX: 15,
        fontPx: landscape ? 14 : 15,
        onClick: () => {
          topic = label.slice(0, MAX_TOPIC_LEN);
          message?.setText('');
          paintSlot();
        },
      });
      const w = (ch.getData('w') as number) ?? 0;
      if (cx + w > right && cx > left) {
        if (rows >= maxRows) {
          ch.destroy();
          break;
        }
        cx = left;
        cy += rowGap;
        rows += 1;
        ch.setX(cx);
        ch.setY(cy);
      }
      card.add(ch);
      cx += w + 10;
      bottom = Math.max(bottom, cy + 19);
    }
    return bottom;
  };

  const addStepRow = (left: number, right: number, y: number, step: LabStep, compact = false): void => {
    const compactPx = landscape ? 11 : 12;
    const ix = left + (compact ? 17 : 20);
    card.add(icon(scene, step.iconName, ix, y - 3, compact ? 18 : 20, step.color));
    const tx = left + (compact ? 42 : 50);
    card.add(
      scene.add
        .text(tx, y - (compact ? 9 : 10), step.title.toUpperCase(), {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(compact ? compactPx : 12, 10)}px`,
          color: hex(step.color),
          fontStyle: 'bold',
        })
        .setOrigin(0, 0.5),
    );
    card.add(
      scene.add
        .text(tx, y + (compact ? 10 : 11), step.body, {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(compact ? compactPx : 12, 10)}px`,
          color: hex(PALETTE.dim),
          wordWrap: { width: right - tx - 8, useAdvancedWrap: true },
        })
        .setOrigin(0, 0.5),
    );
  };

  const addMiniTile = (x: number, y: number, w: number, title: string, value: string, color: number): void => {
    const h = 58;
    const g = scene.add.graphics();
    g.fillStyle(PALETTE.tile, 0.88);
    g.fillRoundedRect(x - w / 2, y - h / 2, w, h, 10);
    g.lineStyle(1, color, 0.75);
    g.strokeRoundedRect(x - w / 2, y - h / 2, w, h, 10);
    card.add(g);
    card.add(
      scene.add
        .text(x, y - 10, value.toUpperCase(), {
          fontFamily: FONT.display,
          fontSize: `${scaledFont(14, 11)}px`,
          color: hex(color),
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );
    card.add(labelText(scene, x, y + 14, title, { size: 9, origin: 0.5 }));
  };

  const addMobileContractCard = (left: number, right: number, top: number): number => {
    const w = right - left;
    const h = 276;
    const cx = (left + right) / 2;
    card.add(cardPanel(scene, cx, top + h / 2, w, h, { fill: PALETTE.surface, border: PALETTE.lineStrong, accent: PALETTE.correct }));
    card.add(labelText(scene, left + 18, top + 24, 'CUSTOM BOARD CONTRACT', { size: 12, color: PALETTE.correct }));

    const tileGap = 8;
    const tileW = (w - 36 - tileGap * 2) / 3;
    const tileY = top + 68;
    addMiniTile(left + 18 + tileW / 2, tileY, tileW, 'Prompt', '1 topic', PALETTE.scan);
    addMiniTile(left + 18 + tileW * 1.5 + tileGap, tileY, tileW, 'Verifier', 'required', PALETTE.correct);
    addMiniTile(left + 18 + tileW * 2.5 + tileGap * 2, tileY, tileW, 'Result', 'share', PALETTE.gold);

    const rowTop = top + 134;
    TRUST_STEPS.forEach((step, i) => addStepRow(left + 14, right - 14, rowTop + i * 38, step, true));
    return top + h;
  };

  const addShareGuard = (left: number, right: number, top: number): number => {
    const w = right - left;
    const h = 76;
    const cx = (left + right) / 2;
    card.add(cardPanel(scene, cx, top + h / 2, w, h, { fill: PALETTE.surface2, border: PALETTE.gold, accent: PALETTE.gold }));
    card.add(icon(scene, 'lightbulb', left + 24, top + 26, 18, PALETTE.armed));
    card.add(labelText(scene, left + 46, top + 24, 'AFTER THE ROUND', { size: 11, color: PALETTE.gold }));
    card.add(
      scene.add
        .text(left + 46, top + 50, 'Share a challenge link with this exact verified board. Failed verification never opens play.', {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(12, 10)}px`,
          color: hex(PALETTE.dim),
          wordWrap: { width: w - 68, useAdvancedWrap: true },
        })
        .setOrigin(0, 0.5),
    );
    return top + h;
  };

  const addTrustCockpit = (left: number, right: number, top: number, h: number): void => {
    const w = right - left;
    const cx = (left + right) / 2;
    card.add(cardPanel(scene, cx, top + h / 2, w, h, { fill: PALETTE.surface, border: PALETTE.lineStrong, accent: PALETTE.correct }));
    card.add(labelText(scene, left + 18, top + 28, 'CUSTOM BOARD CONTRACT', { size: 12, color: PALETTE.correct }));
    card.add(
      scene.add
        .text(left + 18, top + 58, 'No blind trivia. Every custom run passes through the same trust stack before the race starts.', {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(13, 11)}px`,
          color: hex(PALETTE.dim),
          wordWrap: { width: w - 36, useAdvancedWrap: true },
        })
        .setOrigin(0, 0.5),
    );

    const tileGap = 10;
    const tileW = (w - 36 - tileGap * 2) / 3;
    const tileY = top + 112;
    addMiniTile(left + 18 + tileW / 2, tileY, tileW, 'Prompt', '1 topic', PALETTE.scan);
    addMiniTile(left + 18 + tileW * 1.5 + tileGap, tileY, tileW, 'Verifier', 'required', PALETTE.correct);
    addMiniTile(left + 18 + tileW * 2.5 + tileGap * 2, tileY, tileW, 'Result', 'share', PALETTE.gold);

    const rowTop = top + 176;
    TRUST_STEPS.forEach((step, i) => addStepRow(left + 16, right - 16, rowTop + i * 43, step, true));
  };

  const addDesktopFooterStrip = (left: number, right: number, top: number): number => {
    const w = right - left;
    const h = 78;
    const cx = (left + right) / 2;
    card.add(cardPanel(scene, cx, top + h / 2, w, h, { fill: PALETTE.surface2, border: PALETTE.line, accent: PALETTE.gold }));
    card.add(icon(scene, 'lightbulb', left + 24, top + h / 2, 18, PALETTE.armed));
    card.add(
      scene.add
        .text(left + 52, top + 28, 'After the round, SHARE challenges a friend with your exact board.', {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(13, 11)}px`,
          color: hex(PALETTE.text),
          fontStyle: '600',
        })
        .setOrigin(0, 0.5),
    );
    card.add(
      scene.add
        .text(left + 52, top + 52, 'Custom topics use the normal build, verify, race path; failed verification never opens play.', {
          fontFamily: FONT.family,
          fontSize: `${scaledFont(12, 10)}px`,
          color: hex(PALETTE.dim),
          wordWrap: { width: w - 86, useAdvancedWrap: true },
        })
        .setOrigin(0, 0.5),
    );
    return top + h;
  };

  // ── BUILD & PLAY (single primary CTA; ✕ closes) ──
  const build = (): void => {
    const base = topic.trim();
    if (base.length < 2) {
      message?.setText('Type a factual topic first — e.g. "Dinosaurs", "US Presidents".').setColor(hex(PALETTE.armed));
      editTopic();
      return;
    }
    const finalTopic = (base + TWISTS[twistIdx].suffix).slice(0, MAX_TOPIC_LEN + TWISTS[twistIdx].suffix.length);
    rememberCustomRecent(base);
    audio().playSfx('uiConfirm');
    // Close the modal BEFORE launching: playTopic draws its loading veil at a lower depth than this
    // 30k-depth modal, so the panel must be gone first or it would sit on top of the cook screen.
    m.close();
    opts.onLaunch(finalTopic);
  };

  const outerLeft = -cardW / 2 + 22;
  const outerRight = cardW / 2 - 22;
  const panelW = outerRight - outerLeft;
  let ctaY: number;

  if (landscape) {
    const top = bodyTop + 62;
    const gutter = 18;
    const leftW = Math.round((panelW - gutter) * 0.57);
    const left = outerLeft;
    const leftRight = left + leftW;
    const right = leftRight + gutter;
    const rightRight = outerRight;
    const mainH = 400;

    card.add(cardPanel(scene, left + leftW / 2, top + mainH / 2, leftW, mainH, { fill: PALETTE.surface, border: PALETTE.scan, accent: PALETTE.scan }));
    card.add(labelText(scene, left + 18, top + 28, 'TOPIC RECIPE', { size: 12, color: PALETTE.scan }));
    let y = addTopicField(left + 18, leftRight - 18, top + 70);
    y = addTwists(left + 18, leftRight - 18, y + 24);
    addIdeaChips(left + 18, leftRight - 18, y + 34, 2);

    addTrustCockpit(right, rightRight, top, mainH);
    const stripBottom = addDesktopFooterStrip(outerLeft, outerRight, top + mainH + 14);
    ctaY = stripBottom + 48;
  } else {
    const recipeTop = bodyTop + 58;
    const recipeH = 410;
    const recipeLeft = outerLeft + 20;
    const recipeRight = outerRight - 20;
    card.add(cardPanel(scene, 0, recipeTop + recipeH / 2, panelW, recipeH, { fill: PALETTE.surface, border: PALETTE.scan, accent: PALETTE.scan }));
    card.add(labelText(scene, outerLeft + 18, recipeTop + 26, 'TOPIC RECIPE', { size: 12, color: PALETTE.scan }));
    let y = addTopicField(recipeLeft, recipeRight, recipeTop + 70);
    y = addTwists(recipeLeft, recipeRight, y + 22);
    y = addIdeaChips(recipeLeft, recipeRight, y + 32, 2) + 28;
    y = Math.max(y, recipeTop + recipeH + 14);
    y = addMobileContractCard(outerLeft + 2, outerRight - 2, y) + 12;
    y = addShareGuard(outerLeft + 2, outerRight - 2, y);
    ctaY = y + 54;
  }

  card.add(ctaButton(scene, 0, ctaY, 'BUILD & PLAY', () => build(), { width: panelW, icon: 'flask', sfx: null }));
  m.relayout(ctaY + 42 - bodyTop);

  return m;
}
